import React from 'react';
import SearchableSelect from '../../common/SearchableSelect';
import { useI18n } from '../../../contexts/I18nContext';
import { hasOverride, overriddenKeysOf } from '../../../services/deviceService';
import { DeviceDetailCore } from '../useDeviceDetail';
import { CONF47_TYPES, useDevicesDetailsBasic } from './useDevicesDetailsBasic';
import PhoneRecoveryPanel from './PhoneRecoveryPanel';
import './DevicesDetailsBasic.css';

interface Props {
  core: DeviceDetailCore;
}

/** The device row, its dates, its firmware and the name of its template — the panel the dialog opens on. */
export const DevicesDetailsBasic: React.FC<Props> = ({ core }) => {
  const { t, formatDateTime, formatRelativeTime } = useI18n();
  const { device, deviceDetail, template, hardwareTypes, reloadDetail } = core;
  const b = useDevicesDetailsBasic(core);
  const [recovering, setRecovering] = React.useState(false);

  
  if (!device) return null;

  const typeCode = deviceDetail?.deviceType || device.deviceType;
  const currentType = hardwareTypes.find((hw) => hw.code === typeCode);
  const clientId = deviceDetail?.idClient || device.idClient;
  const assignedClient = b.clients.find((c) => c.id === clientId);
  const phone = deviceDetail?.phone ?? device.phone;
  const hasPhone = Boolean(phone && phone !== '000000000000000');
  const missingLocation = !!deviceDetail && !deviceDetail.address && !deviceDetail.postal_code;
  const needsRefresh = deviceDetail?.needsRefresh ?? device.needsRefresh;
  const overriddenCount = overriddenKeysOf(deviceDetail).length;

  const dash = (value: unknown) => (value === null || value === undefined || value === '' ? '-' : String(value));

  return (
    <div className="modal-content device-sections">
      <div className="section-actions">
        {b.isEditing ? (
          <>
            <button className="btn btn-secondary btn-sm" data-testid="btn-device-cancel" onClick={b.cancelEdit} disabled={b.saving}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-success btn-sm" data-testid="btn-device-save" onClick={b.handleSave} disabled={b.saving}>
              {b.saving ? t('devices.saving') : t('devices.saveChanges')}
            </button>
          </>
        ) : (
          <button className="btn btn-secondary btn-sm" data-testid="btn-device-edit" onClick={() => b.setIsEditing(true)}>
            {t('common.edit')}
          </button>
        )}
      </div>

      {/* Section 1: the device row */}
      <div className="device-section device-identity-section">
        <h3 className="section-title">{t('devices.deviceIdentity')}</h3>
        {missingLocation && !b.isEditing && (
          <div className="field-notice" role="note" data-testid="notice-missing-location">
            <span className="field-notice-icon" aria-hidden="true">i</span>
            <span>{t('devices.locationNotice')}</span>
            <button type="button" className="field-notice-link" onClick={() => b.setIsEditing(true)}>{t('common.edit')}</button>
          </div>
        )}
        <div className="identity-fields">
          <div className="identity-field">
            <label>{t('devices.deviceId')}</label>
            <span className="field-value monospace">{device.idDevice}</span>
          </div>

          <div className="identity-field">
            <label>{t('devices.clientLabel')}</label>
            {b.isEditing ? (
              <SearchableSelect
                value={b.selectedClientId}
                onChange={b.setSelectedClientId}
                className="field-select"
                placeholder={t('devices.selectClient')}
                options={b.clients.filter((c) => c.active !== false).map((client) => ({ value: String(client.id), label: client.name }))}
              />
            ) : (
              <span className="field-value">
                {b.clientName || clientId || '-'}
                {assignedClient && assignedClient.active === false && (
                  <span className="status-badge status-inactive" style={{ marginLeft: 8 }}>{t('devices.inactive')}</span>
                )}
              </span>
            )}
          </div>

          <div className="identity-field">
            <label>{t('devices.typeLabel')}</label>
            {b.isEditing ? (
              <SearchableSelect
                value={b.selectedTypeCode}
                onChange={(next) => {
                  b.setSelectedTypeCode(next);
                  b.setSelectedModelId('');
                }}
                className="field-select"
                placeholder={t('devices.selectType')}
                options={hardwareTypes.filter((hw) => hw.is_active).map((type) => ({ value: type.code, label: `${type.name} (${type.code})` }))}
              />
            ) : (
              <span className="field-value">
                {currentType?.name || typeCode || '-'}
                {currentType && !currentType.is_active && (
                  <span className="status-badge status-inactive" style={{ marginLeft: 8 }}>{t('devices.inactive')}</span>
                )}
              </span>
            )}
          </div>

          <div className="identity-field">
            <label>{t('devices.modelLabel')}</label>
            {b.isEditing ? (
              <SearchableSelect
                value={b.selectedModelId}
                onChange={b.setSelectedModelId}
                className="field-select"
                disabled={!b.selectedTypeCode || b.modelRefs.length === 0}
                placeholder={t('devices.selectModel')}
                options={b.modelRefs.filter((r) => r.is_active).map((ref) => ({ value: String(ref.id), label: `${ref.code}${ref.name ? ` - ${ref.name}` : ''}` }))}
              />
            ) : (
              <span className="field-value">
                {b.currentModel ? `${b.currentModel.code}${b.currentModel.name ? ` - ${b.currentModel.name}` : ''}` : dash(deviceDetail?.hardware_model_id)}
              </span>
            )}
          </div>

          <div className="identity-field">
            <label>{t('devices.phone')}</label>
            {b.isEditing ? (
              <input
                type="text"
                value={b.editedPhone}
                onChange={(e) => b.setEditedPhone(e.target.value.replace(/\D/g, ''))}
                className="field-input"
                placeholder={t('devices.phonePlaceholder')}
                maxLength={15}
              />
            ) : (
              <span className="field-value monospace">
                {hasPhone ? phone : '-'}
                {!hasPhone && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-xs field-inline-action"
                    onClick={() => setRecovering((v) => !v)}
                    data-testid="btn-phone-recovery"
                  >
                    {t('devices.phoneRecovery.open')}
                  </button>
                )}
              </span>
            )}
          </div>

          <div className="identity-field">
            <label>{t('devices.address')}</label>
            {b.isEditing ? (
              <input
                type="text"
                value={b.editedAddress}
                onChange={(e) => b.setEditedAddress(e.target.value)}
                className="field-input"
                placeholder={t('devices.addressPlaceholder')}
              />
            ) : (
              <span className="field-value">{dash(deviceDetail?.address)}</span>
            )}
          </div>

          <div className="identity-field">
            <label>{t('devices.postalCode')}</label>
            {b.isEditing ? (
              <input
                type="text"
                value={b.editedPostalCode}
                onChange={(e) => b.setEditedPostalCode(e.target.value.slice(0, 10))}
                className="field-input"
                placeholder={t('devices.postalCodePlaceholder')}
                maxLength={10}
              />
            ) : (
              <span className="field-value monospace">{dash(deviceDetail?.postal_code)}</span>
            )}
          </div>

          <div className="identity-field">
            <label>{t('devices.conf47Type')}</label>
            {b.isEditing ? (
              <SearchableSelect
                value={b.editedConf47Type}
                onChange={b.setEditedConf47Type}
                className="field-select"
                placeholder={t('devices.conf47Type')}
                options={CONF47_TYPES.map((v) => ({ value: v, label: v }))}
              />
            ) : (
              <span className="field-value monospace">{dash(deviceDetail?.conf47Type ?? device.conf47Type)}</span>
            )}
          </div>

          <div className="identity-field">
            <label>{t('devices.voiceDestinations')}</label>
            {b.isEditing ? (
              <input
                type="text"
                value={b.editedVoiceDsts}
                onChange={(e) => b.setEditedVoiceDsts(e.target.value)}
                className="field-input"
                placeholder={t('devices.voiceDestinationsPlaceholder')}
              />
            ) : (
              <span className="field-value monospace">
                {deviceDetail?.mqtt_voice_dsts && deviceDetail.mqtt_voice_dsts.length > 0 ? deviceDetail.mqtt_voice_dsts.join(', ') : '-'}
              </span>
            )}
          </div>

          <div className="identity-field">
            <label>{t('devices.status')}</label>
            {b.isEditing ? (
              <label className="field-switch">
                <input
                  type="checkbox"
                  role="switch"
                  checked={b.editedNeedsRefresh}
                  onChange={(e) => b.setEditedNeedsRefresh(e.target.checked)}
                  aria-checked={b.editedNeedsRefresh}
                  data-testid="field-device-needs-refresh"
                />
                <span className="field-switch-track" aria-hidden="true"><span className="field-switch-knob" /></span>
                <span className="field-switch-text">
                  <span className="field-switch-label">{t('devices.needsRefresh')}</span>
                  <span className="field-switch-hint">{t('devices.needsRefreshHint')}</span>
                </span>
              </label>
            ) : (
              <span className="field-value">
                <span className={`status-badge ${needsRefresh ? 'needs-refresh' : 'up-to-date'}`}>
                  {needsRefresh ? t('devices.needsRefresh') : t('devices.upToDate')}
                </span>
              </span>
            )}
          </div>
        </div>

        {recovering && !hasPhone && !b.isEditing && (
          <PhoneRecoveryPanel
            deviceId={device.idDevice}
            onClose={() => setRecovering(false)}
            onSaved={() => { setRecovering(false); reloadDetail(); }}
          />
        )}
      </div>

      {/* Section 2: dates */}
      <div className="device-section dates-section">
        <h3 className="section-title">{t('devices.dates')}</h3>
        <div className="identity-fields">
          <div className="identity-field">
            <label>{t('devices.createdAt')}</label>
            <span className="field-value">{deviceDetail?.date_time ? formatDateTime(deviceDetail.date_time) : '-'}</span>
          </div>
          <div className="identity-field">
            <label>{t('devices.lastUpdated')}</label>
            <span className="field-value">{deviceDetail?.lastUpdateDateTime ? formatDateTime(deviceDetail.lastUpdateDateTime) : '-'}</span>
          </div>
          <div className="identity-field">
            <label>{t('devices.lastRequest')}</label>
            <span className="field-value">
              {deviceDetail?.last_request_at
                ? `${formatRelativeTime(deviceDetail.last_request_at)}${deviceDetail.last_request_by ? ` · ${deviceDetail.last_request_by}` : ''}`
                : t('devices.never')}
            </span>
          </div>
        </div>
      </div>

      {/* Section 3: firmware */}
      <div className="device-section firmware-section">
        <h3 className="section-title">{t('devices.firmware')}</h3>
        <div className="firmware-content">
          {b.firmwareLoading ? (
            <span className="firmware-loading">{t('devices.firmwareLoading')}</span>
          ) : b.firmwareVersions.length > 0 ? (
            <div className="firmware-versions">
              <label>{t('devices.firmwareVersions')}</label>
              <span className="firmware-list">{b.firmwareSummary}</span>
            </div>
          ) : (
            <span className="no-firmware">{t('devices.noFirmwareData')}</span>
          )}
        </div>
      </div>

      {/* Section 4: the template, by name only — its values are in Settings */}
      <div className="device-section config-section">
        <h3 className="section-title">{t('devices.configurationTemplate')}</h3>
        <div className="config-normal">
          <div className="template-controls">
            <SearchableSelect
              value={b.selectedTemplateId != null ? String(b.selectedTemplateId) : ''}
              onChange={(next) => b.setSelectedTemplateId(next ? Number(next) : null)}
              disabled={b.templatesLoading || b.applying || b.resetting}
              className="template-select"
              placeholder={t('devices.noTemplate')}
              options={b.templates.map((template) => ({ value: String(template.id), label: template.name }))}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={b.handleApplyTemplate}
              disabled={!b.selectedTemplateId || b.applying || b.resetting || !b.hasChangedTemplate}
            >
              {b.applying ? t('devices.applying') : b.hasChangedTemplate ? t('devices.applyTemplate') : t('devices.applied')}
            </button>
          </div>

          {deviceDetail?.template_id && template ? (
            <div className="identity-fields template-info-grid">
              <div className="identity-field">
                <label>{t('devices.currentTemplate')}</label>
                <span className="field-value">
                  {template.name}
                  {template.is_default && <span className="status-badge up-to-date" style={{ marginLeft: 8 }}>{t('devices.templateDefault')}</span>}
                  {!template.is_active && <span className="status-badge status-inactive" style={{ marginLeft: 8 }}>{t('devices.inactive')}</span>}
                </span>
              </div>
              <div className="identity-field">
                <label>{t('devices.templateDescription')}</label>
                <span className="field-value">{dash(template.description)}</span>
              </div>
              <div className="identity-field">
                <label>{t('devices.createdAt')}</label>
                <span className="field-value">{template.created_at ? formatDateTime(template.created_at) : '-'}</span>
              </div>
              <div className="identity-field">
                <label>{t('devices.lastUpdated')}</label>
                <span className="field-value">{template.updated_at ? formatDateTime(template.updated_at) : '-'}</span>
              </div>
              <div className="identity-field">
                <label>{t('devices.templateDevices')}</label>
                <span className="field-value">{dash(template.device_count)}</span>
              </div>
            </div>
          ) : (
            <div className="current-template-info">
              <span className="template-info-label">{t('devices.currentTemplate')}</span>
              <span className="template-info-value">{deviceDetail?.template_id ? `ID: ${deviceDetail.template_id}` : t('devices.noTemplate')}</span>
            </div>
          )}

          {deviceDetail?.template_id && hasOverride(deviceDetail) && (
            <div className="current-template-info">
              <span className="template-info-label">{t('devices.overriddenKeys', { count: overriddenCount })}</span>
              <button className="btn btn-secondary btn-sm" onClick={b.handleResetToTemplate} disabled={b.resetting || b.applying}>
                {b.resetting ? t('devices.resetting') : t('devices.resetToTemplate')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
